// src/features/passwords/components/ChildRows.jsx
import React from 'react';
import { Copy, Edit } from 'lucide-react';
import SearchableSelect from '../../user-dashboard/components/SearchableSelect';
import ProxyDisplay, { MlProfileStatus } from './ProxyDisplay';
import PersonaSelfTransferCells from './PersonaSelfTransferCells';
import api from '../../../api';
import { shouldShowActivate } from '../../../shared/portActivation';

function matchingPortForPhone(ports, phoneNumber) {
  const digits = (s) => String(s || '').replace(/\D/g, '');
  const phoneDigits = digits(phoneNumber);
  if (!phoneDigits) return null;
  const matches = (ports || []).filter((p) => digits(p.assigned_number) === phoneDigits);
  return matches.find((p) => shouldShowActivate(p)) || matches[0] || null;
}

export default function ChildRow({
  userCode,
  entry,
  isEditing,

  // editing
  editDraft,
  onChange,
  onCancel,
  onSave,
  onSaveApplyAll,

  // start editing this specific row
  onEdit,

  // address editor
  showAddressEditor,
  setShowAddressEditor,
  addressParts,
  setAddressParts,
  onAddressSave,

  // actions
  onCopy,
  onActivatePort,
  onDelete,

  // visuals
  showMultilogin = false,
  multiloginFlag = false,
  isExpandedRow = false,
  getStatusBadge,

  // extras
  renderApplyAllExtras,

  // ports so we can match phone → port
  ports,

  // sites for dropdown
  sites = [],
  isStaff = false,
  onRequestSite = null,

  // refetch function for refreshing data
  refetch,
  // inline multilogin toggle
  onToggleMultilogin,
  multiloginSaving = false,
  onToggleMlEnvironment,
  mlEnvironment = null,

  // persona check / self transfer (advice passwords)
  showPersonaSelfTransfer = false,
  onPersonaCheckSubmit = null,
  onPersonaCheckClear = null,
  onSelfTransferToggle = null,
}) {
  const phoneNumber = entry.phone_number;

  // ─────────────────────────────────────────────
  // EDIT MODE
  // Column order must match parent header:
  // User Code | Full Name | (Multilogin) | Email | Phone | Site | Password | Proxy | Address | DOB | Actions
  // ─────────────────────────────────────────────
  if (isEditing) {
    return (
      <tr
        className={`align-middle ${isExpandedRow ? 'table-secondary' : 'bg-light'}`}
        style={isExpandedRow ? { borderLeft: '3px solid #6c757d' } : {}}
      >
        {/* User Code */}
        <td>{userCode}</td>

        {/* Full name */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={editDraft.fullName}
            onChange={(e) => onChange('fullName', e.target.value)}
          />
        </td>

        {/* Multilogin */}
        {showMultilogin && (
          <td onClick={(e) => e.stopPropagation()}>
            <div className="form-check form-switch m-0">
              <input
                className="form-check-input"
                type="checkbox"
                checked={!!multiloginFlag}
                disabled={!!multiloginSaving}
                onChange={(e) => onToggleMultilogin?.(e.target.checked)}
                style={{ cursor: multiloginSaving ? 'wait' : 'pointer' }}
                title={multiloginSaving ? 'Saving...' : (multiloginFlag ? 'Multilogin ON' : 'Multilogin OFF')}
              />
            </div>
            <MlProfileStatus
              multilogin={multiloginFlag}
              mlProfile={entry.ml_profile}
            />
          </td>
        )}

        {/* Email */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={editDraft.email}
            onChange={(e) => onChange('email', e.target.value)}
          />
        </td>

        {/* Phone */}
        <td>
          <div className="d-flex flex-column gap-1">
            {/* System-managed phone number (from ports) */}
            {phoneNumber && (
              <div className="d-flex align-items-center gap-1">
                <span>{phoneNumber}</span>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCopy?.(phoneNumber);
                  }}
                  title="Copy phone"
                >
                  <Copy size={14} />
                </button>
                {(() => {
                  const matchingPort = matchingPortForPhone(ports, phoneNumber);
                  if (!shouldShowActivate(matchingPort)) return null;
                  return (
                <button
                  type="button"
                  className="btn btn-sm btn-success ms-2"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const btn = e.currentTarget;
                    const originalText = btn.innerHTML;
                    try {
                      btn.disabled = true;
                      btn.innerHTML = '<span class="spinner-border spinner-border-sm"></span>';
                      await onActivatePort?.(phoneNumber);
                    } finally {
                      btn.disabled = false;
                      btn.innerHTML = originalText;
                    }
                  }}
                  title="Activate port"
                  style={{ fontSize: '11px', padding: '2px 8px' }}
                >
                  Activate
                </button>
                  );
                })()}
              </div>
            )}
            
            {/* User-managed numbers (editable in edit mode) */}
            {editDraft.user_managed_numbers && Array.isArray(editDraft.user_managed_numbers) && editDraft.user_managed_numbers.length > 0 && (
              <>
                {editDraft.user_managed_numbers.map((num, idx) => {
                  const digits = (s) => String(s || '').replace(/\D/g, '');
                  const numDigits = digits(num);
                  // Only show if this number is not the same as phoneNumber (to avoid duplicates)
                  const isDuplicate = phoneNumber && digits(phoneNumber) === numDigits;
                  if (isDuplicate) return null;
                  
                  return (
                    <div key={idx} className="d-flex align-items-center gap-1">
                      <span 
                        style={{ 
                          color: '#dc3545', 
                          fontWeight: '600',
                          fontSize: '13px'
                        }} 
                        title="User-managed number (not on our system)"
                      >
                        {num}
                      </span>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 ms-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCopy?.(num);
                        }}
                        title="Copy phone"
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ms-1"
                        style={{ fontSize: '10px', padding: '1px 4px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          const newNumbers = editDraft.user_managed_numbers.filter((_, i) => i !== idx);
                          onChange('user_managed_numbers', newNumbers);
                        }}
                        title="Remove number"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </>
            )}
            
            {/* Add user-managed number (only in edit mode) */}
            {isStaff && (
              <div className="mt-1">
                <small className="text-muted d-block mb-1" style={{ fontSize: '10px' }}>
                  💡 User-Managed SIMs: Add phone numbers not on our system (displayed in red)
                </small>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  style={{ 
                    fontSize: '11px', 
                    padding: '4px 8px', 
                    fontWeight: '600',
                    borderWidth: '2px'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const num = prompt('Enter phone number for user-managed SIM (will be displayed in red):');
                    if (num && num.trim()) {
                      const current = editDraft.user_managed_numbers || [];
                      const updated = [...current, num.trim()];
                      onChange('user_managed_numbers', updated);
                    }
                  }}
                  title="Add user-managed phone number (not on our system)"
                >
                  + Add User-Managed Number
                </button>
              </div>
            )}
          </div>
        </td>

        {/* Site */}
        <td>
          <SearchableSelect
            value={editDraft.site}
            onChange={(value) => onChange('site', value)}
            options={sites.map(site => ({ value: site, label: site }))}
            placeholder="Site"
            allowCustom={isStaff}
            onRequestSite={onRequestSite}
            className="form-select form-select-sm"
          />
        </td>

        {/* Password */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm font-monospace"
            value={editDraft.password}
            onChange={(e) => onChange('password', e.target.value)}
          />
        </td>

        {/* Proxy — must come BEFORE Address to match header */}
        <td>
          <input
            type="text"
            className="form-control form-control-sm"
            value={editDraft.proxy || ''}
            onChange={(e) => onChange('proxy', e.target.value)}
            placeholder="Proxy..."
          />
        </td>

        {showPersonaSelfTransfer && (
          <PersonaSelfTransferCells
            entry={entry}
            onPersonaCheckSubmit={onPersonaCheckSubmit}
            onPersonaCheckClear={onPersonaCheckClear}
            onSelfTransferToggle={onSelfTransferToggle}
          />
        )}

        {/* Address */}
        <td>
          <div
            className="form-control form-control-sm"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowAddressEditor?.(true)}
            title="Click to edit address"
          >
            {editDraft.address || 'No address available'}
          </div>

          {showAddressEditor && (
            <div className="mt-2 p-2 bg-light border rounded">
              <h6 className="mb-2">Edit Address</h6>

              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Line 1"
                    value={addressParts.line1}
                    onChange={(e) => setAddressParts((p) => ({ ...p, line1: e.target.value }))}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Town/City"
                    value={addressParts.town}
                    onChange={(e) => setAddressParts((p) => ({ ...p, town: e.target.value }))}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Postcode"
                    value={addressParts.postcode}
                    onChange={(e) => setAddressParts((p) => ({ ...p, postcode: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Line 2"
                    value={addressParts.line2}
                    onChange={(e) => setAddressParts((p) => ({ ...p, line2: e.target.value }))}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="County/State"
                    value={addressParts.county}
                    onChange={(e) => setAddressParts((p) => ({ ...p, county: e.target.value }))}
                  />
                  <input
                    type="text"
                    className="form-control form-control-sm mb-2"
                    placeholder="Country"
                    value={addressParts.country}
                    onChange={(e) => setAddressParts((p) => ({ ...p, country: e.target.value }))}
                  />
                </div>
              </div>

              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary me-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddressSave();
                  }}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAddressEditor?.(false);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </td>

        {/* DOB */}
        <td>
          <input
            type="date"
            className="form-control form-control-sm"
            value={editDraft.dob || ''}
            onChange={(e) => onChange('dob', e.target.value)}
          />
        </td>

        {/* Actions */}
        <td>
          <button
            type="button"
            className="btn btn-primary btn-sm me-1"
            onClick={(e) => {
              e.stopPropagation();
              onSave?.(entry);
            }}
          >
            Save
          </button>
          {typeof onSaveApplyAll === 'function' && (
            <>
              <button
                type="button"
                className="btn btn-warning btn-sm me-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onSaveApplyAll?.(entry);
                }}
              >
                Save + Apply All
              </button>
              {typeof renderApplyAllExtras === 'function' && renderApplyAllExtras()}
            </>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              onCancel?.();
            }}
          >
            Cancel
          </button>
        </td>
      </tr>
    );
  }

  // ─────────────────────────────────────────────
  // VIEW MODE
  // Must match same order as header + edit mode
  // ─────────────────────────────────────────────
  return (
    <tr
      className={`align-middle ${isExpandedRow ? 'table-secondary' : ''}`}
      style={isExpandedRow ? { borderLeft: '3px solid #6c757d' } : {}}
    >
      {/* User Code */}
      <td>
        {userCode}{' '}
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-1"
          onClick={(e) => {
            e.stopPropagation();
            onCopy?.(userCode);
          }}
          title="Copy user code"
        >
          <Copy size={14} />
        </button>
      </td>

      {/* Full name */}
      <td>{entry.full_name}</td>

      {/* Multilogin */}
      {showMultilogin && (
        <td onClick={(e) => e.stopPropagation()}>
          <div className="form-check form-switch m-0">
            <input
              className="form-check-input"
              type="checkbox"
              checked={!!multiloginFlag}
              disabled={!!multiloginSaving}
              onChange={(e) => onToggleMultilogin?.(e.target.checked)}
              style={{ cursor: multiloginSaving ? 'wait' : 'pointer' }}
              title={multiloginSaving ? 'Saving...' : (multiloginFlag ? 'Multilogin ON' : 'Multilogin OFF')}
            />
          </div>
          <MlProfileStatus
            multilogin={multiloginFlag}
            mlProfile={entry.ml_profile}
          />
        </td>
      )}

      {/* Email */}
      <td>
        {entry.email && (
          <>
            {entry.email}{' '}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 ms-1"
              onClick={(e) => { e.stopPropagation(); onCopy?.(entry.email); }}
              title="Copy email"
            >
              <Copy size={14} />
            </button>
          </>
        )}
        {/* ML Environment toggle — right of email */}
        {showMultilogin && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-2"
            onClick={(e) => {
              e.stopPropagation();
              const next = !mlEnvironment ? 'apple' : mlEnvironment === 'apple' ? 'windows' : null;
              onToggleMlEnvironment?.(next);
            }}
            title={
              !mlEnvironment ? 'Set ML environment (click to set Apple)' :
              mlEnvironment === 'apple' ? 'Apple ML environment (click for Windows)' :
              'Windows ML environment (click to clear)'
            }
            style={{ lineHeight: 1, opacity: mlEnvironment ? 1 : 0.2 }}
          >
            {(!mlEnvironment || mlEnvironment === 'apple') && (
              <svg width="13" height="14" viewBox="0 0 384 512" fill="currentColor">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
              </svg>
            )}
            {mlEnvironment === 'windows' && (
              <svg width="14" height="14" viewBox="0 0 448 512" fill="currentColor">
                <path d="M0 93.7l183.6-25.3v177H0V93.7zm0 324.6l183.6 25.3V269.7H0v148.6zM204.8 66.4L448 32v212.7H204.8V66.4zM448 479.6L204.8 445.6V269.7H448v209.9z"/>
              </svg>
            )}
          </button>
        )}
      </td>

      {/* Phone */}
      <td>
        <div className="d-flex flex-column gap-1">
          {/* System-managed phone numbers (from ports) */}
          {phoneNumber &&
            (() => {
              const digits = (s) => String(s || '').replace(/\D/g, '');
              const phoneDigits = digits(phoneNumber);
              const matchingPort = matchingPortForPhone(ports, phoneNumber);
              
              // Check if this phone number is user-managed (not in port management)
              const isUserManaged = entry.user_managed_numbers && 
                Array.isArray(entry.user_managed_numbers) && 
                entry.user_managed_numbers.some(num => digits(num) === phoneDigits);

              return (
                <div className="d-flex align-items-center gap-1">
                  <span 
                    style={{ 
                      color: isUserManaged ? '#dc3545' : 'inherit',
                      fontWeight: isUserManaged ? '600' : 'inherit'
                    }} 
                    title={isUserManaged ? "User-managed number (not on our system)" : ""}
                  >
                    {phoneNumber}
                  </span>
                  {getStatusBadge && matchingPort && getStatusBadge(matchingPort)}
                  {!matchingPort && (
                    <span className="text-danger" style={{ fontSize: '10px' }}>
                      ⚠ No Port
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopy?.(phoneNumber);
                    }}
                    title="Copy phone"
                  >
                    <Copy size={14} />
                  </button>
                  {!isUserManaged && shouldShowActivate(matchingPort) && (
                    <button
                      type="button"
                      className="btn btn-sm btn-success ms-2"
                      onClick={async (e) => {
                        e.stopPropagation();
                        const btn = e.currentTarget;
                        const originalText = btn.innerHTML;
                        try {
                          btn.disabled = true;
                          btn.innerHTML =
                            '<span class="spinner-border spinner-border-sm" role="status"></span> Activating...';
                          await onActivatePort?.(phoneNumber);
                        } finally {
                          btn.disabled = false;
                          btn.innerHTML = originalText;
                        }
                      }}
                      title="Activate port"
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >
                      Activate
                    </button>
                  )}
                </div>
              );
            })()}
          
          {/* User-managed phone numbers (not on system) - display inline with other numbers */}
          {entry.user_managed_numbers && Array.isArray(entry.user_managed_numbers) && entry.user_managed_numbers.length > 0 && (
            <>
              {entry.user_managed_numbers.map((num, idx) => {
                const digits = (s) => String(s || '').replace(/\D/g, '');
                const numDigits = digits(num);
                // Only show if this number is not the same as phoneNumber (to avoid duplicates)
                const isDuplicate = phoneNumber && digits(phoneNumber) === numDigits;
                if (isDuplicate) return null;
                
                return (
                  <div key={idx} className="d-flex align-items-center gap-1">
                    <span 
                      style={{ 
                        color: '#dc3545', 
                        fontWeight: '600',
                        fontSize: '13px'
                      }} 
                      title="User-managed number (not on our system)"
                    >
                      {num}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 ms-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy?.(num);
                      }}
                      title="Copy phone"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </td>

      {/* Site */}
      <td>{entry.site}</td>

      {/* Password */}
      <td>
        <span className="font-monospace">{entry.password}</span>{' '}
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-1"
          onClick={(e) => {
            e.stopPropagation();
            onCopy?.(entry.password);
          }}
          title="Copy password"
        >
          <Copy size={14} />
        </button>
      </td>

      {/* Proxy — must be before Address */}
      <td className="proxy-cell">
        <ProxyDisplay
          connectedProxy={entry.connected_proxy}
          proxy={entry.proxy}
          mlProxy={entry.ml_proxy}
          mlFolderName={entry.ml_folder_name}
          mlProfileName={entry.ml_profile_name}
        />
      </td>

      {showPersonaSelfTransfer && (
        <PersonaSelfTransferCells
          entry={entry}
          onPersonaCheckSubmit={onPersonaCheckSubmit}
          onPersonaCheckClear={onPersonaCheckClear}
          onSelfTransferToggle={onSelfTransferToggle}
        />
      )}

      {/* Address */}
      <td>{entry.address || 'No address available'}</td>

      {/* DOB */}
      <td>{entry.dob || 'No DOB available'}</td>

      {/* Actions */}
      <td>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm me-1"
          onClick={(e) => {
            e.stopPropagation();
            onEdit?.(entry);
          }}
          title="Edit"
        >
          <Edit size={14} />
        </button>
        <button
          type="button"
          className="btn btn-outline-danger btn-sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(entry);
          }}
        >
          Delete
        </button>
      </td>
    </tr>
  );
}
